import type { AuthTokens, UserProfile } from "@/api/types";
import { KC_IDP_HINT_PARAM } from "@/lib/constants";
import { captureException } from "@/lib/errors";
import type { AuthStore } from "@/stores/authStore";
import { useServiceDetailAnswersStore } from "@/stores/serviceDetailAnswersStore";
import { posthog, resetPostHogUser } from "./posthog";
import { clearSentryUser } from "./sentry";

const LOGIN_PATH = "/login";
const TOKEN_KEY = "auth_tokens";
const LAST_ACTIVITY_KEY = "last_activity_time";
const INACTIVITY_TIMEOUT_MINUTES = 70;
const INACTIVITY_TIMEOUT_MS = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
let inactivityIntervalId: NodeJS.Timeout | null = null;

const updateLastActivity = (): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

const getLastActivity = (): number => {
  if (typeof window === "undefined") return 0;
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  return lastActivity ? parseInt(lastActivity, 10) : 0;
};

const isInactive = (): boolean => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return false;
  return Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS;
};

const checkInactivity = (): void => {
  if (isInactive()) {
    posthog.capture("inactivity_timeout");
    import("@/stores/authStore").then(({ useAuthStore }) => {
      logout(useAuthStore.getState());
    });
  }
};

const startInactivityTimer = (): void => {
  if (inactivityIntervalId) {
    clearInterval(inactivityIntervalId);
  }

  inactivityIntervalId = setInterval(() => {
    checkInactivity();
  }, 60000);
};

export const trackActivity = (): void => {
  updateLastActivity();
  startInactivityTimer();
};

export const initializeActivityTracking = (): void => {
  if (typeof window === "undefined") return;

  const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

  events.forEach((event) => {
    window.addEventListener(event, trackActivity, { passive: true });
  });

  const lastActivity = getLastActivity();
  if (lastActivity) {
    startInactivityTimer();
  }
};

export const getTokens = (): AuthTokens | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const tokens = localStorage.getItem(TOKEN_KEY);
    if (!tokens) {
      return null;
    }

    if (isInactive()) {
      clearTokens();
      return null;
    }

    return JSON.parse(tokens);
  } catch (error) {
    captureException(error, {
      tags: {
        operation: "auth_token_retrieval",
        context: "localStorage",
      },
    });
    return null;
  }
};

export const setTokens = (tokens: AuthTokens): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    updateLastActivity();
    startInactivityTimer();
  } catch (error) {
    captureException(error, {
      tags: {
        operation: "auth_token_storage",
        context: "localStorage",
      },
    });
  }
};

export const clearTokens = (): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    if (inactivityIntervalId) {
      clearInterval(inactivityIntervalId);
      inactivityIntervalId = null;
    }
  } catch (error) {
    captureException(error, {
      tags: {
        operation: "auth_token_clear",
        context: "localStorage",
      },
    });
  }
};

export const getAccessToken = (): string | null => {
  const tokens = getTokens();
  return tokens?.access || null;
};

export const isAuthenticated = (): boolean => !!getAccessToken();

/** Redirects to /login, preserving kc_idp_hint if present in the current URL. */
export const redirectToLogin = (): void => {
  if (typeof window === "undefined") return;
  const idpHint = new URLSearchParams(window.location.search).get(KC_IDP_HINT_PARAM);
  window.location.href = idpHint
    ? `${LOGIN_PATH}?${KC_IDP_HINT_PARAM}=${encodeURIComponent(idpHint)}`
    : LOGIN_PATH;
};

export const logout = (authStore: AuthStore): void => {
  clearTokens();
  clearSentryUser();
  posthog.capture("user_logged_out");
  resetPostHogUser();
  authStore.logout();
  useServiceDetailAnswersStore.getState().clearAll();
  redirectToLogin();
};

/**
 * Decodes a JWT token and returns the payload
 * @param token - JWT token string
 * @returns Decoded payload object or null if invalid
 */
export const decodeJWT = (token: string): Record<string, unknown> | null => {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    // Decode base64 and parse JSON
    const decodedPayload = JSON.parse(atob(paddedBase64));
    return decodedPayload;
  } catch (error) {
    captureException(error, {
      tags: {
        operation: "auth_jwt_decode",
        context: "token_parsing",
      },
      extra: {
        token_length: token.length,
      },
    });
    return null;
  }
};

/**
 * Gets the current tenant from the stored access token
 * @returns Tenant identifier or null if not available
 */
export const getTenantFromToken = (): string => {
  const token = getAccessToken();
  if (!token) return "";

  const payload = decodeJWT(token);
  return (payload?.tenant as string) || "";
};

/**
 * Get user profile from stored access token
 * @returns User profile or null if not available
 */
export const getUserProfileFromToken = (): UserProfile | null => {
  const token = getAccessToken();
  if (!token) return null;

  const payload = decodeJWT(token);
  const userProfile = {
    email: payload?.email as string,
    firstName: payload?.first_name as string,
    lastName: payload?.last_name as string,
    tenant: payload?.tenant as string,
    role: (payload?.roles as unknown as Array<{ role_name: string }>)?.[0]?.role_name || "",
  };
  return userProfile as UserProfile;
};
